import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Type,
  Image,
  Square,
  Minus,
  Link2,
  Columns,
  Mail,
  GripVertical,
  Trash2,
  Settings,
  Eye,
  Code,
  X,
  Plus,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Save,
  Send,
  FileText,
  Loader2,
  ArrowLeft,
  Copy,
  Palette,
  Layout,
  MousePointer,
  Bold,
  Italic,
  Underline,
  List,
  Heading1,
  Heading2,
  Quote,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email Categories
const EMAIL_CATEGORIES = [
  { value: 'transactional', label: 'Transactional' },
  { value: 'webinar_lifecycle', label: 'Webinar Lifecycle' },
  { value: 'behavior_based', label: 'Behavior Based' },
  { value: 're_engagement', label: 'Re-engagement' },
  { value: 'milestones', label: 'Milestones' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'lead_nurturing', label: 'Lead Nurturing' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'custom', label: 'Custom' },
];

// Component Types
const COMPONENT_TYPES = {
  HEADING: 'heading',
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SPACER: 'spacer',
  COLUMNS_2: 'columns_2',
  COLUMNS_3: 'columns_3',
  SOCIAL: 'social',
  QUOTE: 'quote',
  LIST: 'list',
  VIDEO: 'video',
  FOOTER: 'footer',
  UNSUBSCRIBE: 'unsubscribe',
};

// Default styling presets
const FONT_FAMILIES = [
  { value: "'Plus Jakarta Sans', Arial, sans-serif", label: 'Plus Jakarta Sans' },
  { value: "'Space Grotesk', Arial, sans-serif", label: 'Space Grotesk' },
  { value: "Georgia, serif", label: 'Georgia' },
  { value: "Arial, sans-serif", label: 'Arial' },
  { value: "'Courier New', monospace", label: 'Courier New' },
];

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

const BORDER_STYLES = ['none', 'solid', 'dashed', 'dotted', 'double'];

const PRESET_COLORS = [
  '#1B4F72', '#0F2D44', '#D4A03A', '#B8860B', 
  '#333333', '#666666', '#94a3b8', '#ffffff',
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4',
];

// Default component configurations
const DEFAULT_COMPONENTS = {
  [COMPONENT_TYPES.HEADING]: {
    type: COMPONENT_TYPES.HEADING,
    content: 'Your Heading Here',
    level: 'h1',
    styles: {
      fontFamily: "'Space Grotesk', Arial, sans-serif",
      fontSize: '28px',
      fontWeight: '700',
      color: '#1B4F72',
      textAlign: 'left',
      padding: '10px 0',
      margin: '0',
      lineHeight: '1.3',
      letterSpacing: '0',
    },
  },
  [COMPONENT_TYPES.TEXT]: {
    type: COMPONENT_TYPES.TEXT,
    content: 'Enter your text here. You can customize font, color, alignment, and more.',
    styles: {
      fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
      fontSize: '16px',
      fontWeight: '400',
      color: '#334155',
      textAlign: 'left',
      padding: '10px 0',
      margin: '0',
      lineHeight: '1.6',
      letterSpacing: '0',
      textDecoration: 'none',
      fontStyle: 'normal',
    },
  },
  [COMPONENT_TYPES.IMAGE]: {
    type: COMPONENT_TYPES.IMAGE,
    src: '',
    alt: 'Image',
    width: '100%',
    maxWidth: '600px',
    link: '',
    styles: {
      textAlign: 'center',
      padding: '10px 0',
      borderRadius: '8px',
      border: 'none',
    },
  },
  [COMPONENT_TYPES.BUTTON]: {
    type: COMPONENT_TYPES.BUTTON,
    text: 'Click Here',
    link: '#',
    styles: {
      backgroundColor: '#1B4F72',
      color: '#ffffff',
      padding: '14px 32px',
      borderRadius: '8px',
      textAlign: 'center',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
      border: 'none',
      textDecoration: 'none',
      display: 'inline-block',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    containerStyles: {
      textAlign: 'center',
      padding: '20px 0',
    },
  },
  [COMPONENT_TYPES.DIVIDER]: {
    type: COMPONENT_TYPES.DIVIDER,
    styles: {
      borderColor: '#e2e8f0',
      borderWidth: '1px',
      borderStyle: 'solid',
      margin: '20px 0',
      width: '100%',
    },
  },
  [COMPONENT_TYPES.SPACER]: {
    type: COMPONENT_TYPES.SPACER,
    height: '30px',
    styles: {
      backgroundColor: 'transparent',
    },
  },
  [COMPONENT_TYPES.COLUMNS_2]: {
    type: COMPONENT_TYPES.COLUMNS_2,
    columns: [
      { components: [], width: '50%' },
      { components: [], width: '50%' },
    ],
    styles: {
      gap: '20px',
      padding: '10px 0',
    },
  },
  [COMPONENT_TYPES.COLUMNS_3]: {
    type: COMPONENT_TYPES.COLUMNS_3,
    columns: [
      { components: [], width: '33.33%' },
      { components: [], width: '33.33%' },
      { components: [], width: '33.33%' },
    ],
    styles: {
      gap: '16px',
      padding: '10px 0',
    },
  },
  [COMPONENT_TYPES.SOCIAL]: {
    type: COMPONENT_TYPES.SOCIAL,
    links: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: '',
    },
    iconSize: '32px',
    iconColor: '#1B4F72',
    styles: {
      textAlign: 'center',
      padding: '20px 0',
      gap: '16px',
    },
  },
  [COMPONENT_TYPES.QUOTE]: {
    type: COMPONENT_TYPES.QUOTE,
    content: 'Add an inspiring quote or testimonial here.',
    author: 'Author Name',
    styles: {
      fontFamily: "Georgia, serif",
      fontSize: '18px',
      fontStyle: 'italic',
      color: '#64748b',
      textAlign: 'center',
      padding: '20px',
      margin: '20px 0',
      borderLeft: '4px solid #D4A03A',
      backgroundColor: '#f8fafc',
      borderRadius: '0 8px 8px 0',
    },
  },
  [COMPONENT_TYPES.LIST]: {
    type: COMPONENT_TYPES.LIST,
    items: ['First item', 'Second item', 'Third item'],
    listStyle: 'disc',
    styles: {
      fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
      fontSize: '16px',
      color: '#334155',
      padding: '10px 0 10px 20px',
      lineHeight: '1.8',
    },
  },
  [COMPONENT_TYPES.VIDEO]: {
    type: COMPONENT_TYPES.VIDEO,
    thumbnailUrl: '',
    videoUrl: '',
    playButtonColor: '#ffffff',
    styles: {
      textAlign: 'center',
      padding: '10px 0',
      borderRadius: '8px',
    },
  },
  [COMPONENT_TYPES.FOOTER]: {
    type: COMPONENT_TYPES.FOOTER,
    companyName: 'GRC Educators',
    address: '123 Business St, Suite 100, City, State 12345',
    phone: '',
    email: 'info@grceducators.com',
    styles: {
      textAlign: 'center',
      fontSize: '12px',
      color: '#94a3b8',
      padding: '30px 20px',
      backgroundColor: '#f1f5f9',
      lineHeight: '1.6',
    },
  },
  [COMPONENT_TYPES.UNSUBSCRIBE]: {
    type: COMPONENT_TYPES.UNSUBSCRIBE,
    text: 'Unsubscribe from our emails',
    styles: {
      textAlign: 'center',
      fontSize: '12px',
      color: '#94a3b8',
      padding: '10px 0',
    },
  },
};

// Palette Component
const PaletteItem = ({ type, icon: Icon, label }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'NEW_COMPONENT',
    item: { type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      data-testid={`palette-item-${type}`}
      className={`
        flex flex-col items-center gap-1.5 p-2.5 bg-white border border-slate-200 rounded-lg cursor-grab
        hover:border-navy-400 hover:shadow-sm hover:bg-navy-50 transition-all
        ${isDragging ? 'opacity-50 scale-95' : ''}
      `}
    >
      <Icon className="w-4 h-4 text-slate-600" />
      <span className="text-[10px] text-slate-600 text-center leading-tight">{label}</span>
    </div>
  );
};

// Canvas Component
const CanvasComponent = ({ component, index, moveComponent, removeComponent, selectComponent, isSelected, duplicateComponent }) => {
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: 'CANVAS_COMPONENT',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [index]);

  const [, drop] = useDrop(() => ({
    accept: 'CANVAS_COMPONENT',
    hover: (item) => {
      if (item.index !== index) {
        moveComponent(item.index, index);
        item.index = index;
      }
    },
  }), [index, moveComponent]);

  const renderComponent = () => {
    switch (component.type) {
      case COMPONENT_TYPES.HEADING:
        const HeadingTag = component.level || 'h1';
        return (
          <div style={component.styles}>
            {component.content}
          </div>
        );
      
      case COMPONENT_TYPES.TEXT:
        return (
          <div style={component.styles}>
            {component.content}
          </div>
        );
      
      case COMPONENT_TYPES.IMAGE:
        return (
          <div style={{ textAlign: component.styles.textAlign }}>
            {component.src ? (
              <img 
                src={component.src} 
                alt={component.alt} 
                style={{ 
                  width: component.width, 
                  maxWidth: component.maxWidth || '100%',
                  borderRadius: component.styles.borderRadius,
                  border: component.styles.border,
                }} 
              />
            ) : (
              <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Image className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <span className="text-sm text-slate-500">Add image URL in settings</span>
              </div>
            )}
          </div>
        );
      
      case COMPONENT_TYPES.BUTTON:
        return (
          <div style={component.containerStyles || { textAlign: 'center', padding: '20px 0' }}>
            <button style={component.styles}>
              {component.text}
            </button>
          </div>
        );
      
      case COMPONENT_TYPES.DIVIDER:
        return (
          <hr style={{ 
            border: 'none', 
            borderTop: `${component.styles.borderWidth} ${component.styles.borderStyle} ${component.styles.borderColor}`,
            margin: component.styles.margin,
            width: component.styles.width,
          }} />
        );
      
      case COMPONENT_TYPES.SPACER:
        return (
          <div 
            style={{ height: component.height, backgroundColor: 'rgba(59, 130, 246, 0.05)' }} 
            className="rounded flex items-center justify-center"
          >
            <span className="text-xs text-blue-400">Spacer ({component.height})</span>
          </div>
        );
      
      case COMPONENT_TYPES.COLUMNS_2:
      case COMPONENT_TYPES.COLUMNS_3:
        return (
          <div style={{ display: 'flex', gap: component.styles.gap, padding: component.styles.padding }}>
            {component.columns.map((col, colIdx) => (
              <div 
                key={colIdx} 
                style={{ 
                  flex: 1, 
                  minHeight: '80px', 
                  padding: '12px', 
                  backgroundColor: 'rgba(59, 130, 246, 0.03)', 
                  borderRadius: '8px', 
                  border: '1px dashed #cbd5e1' 
                }}
              >
                <span className="text-xs text-slate-400 font-medium">Column {colIdx + 1}</span>
                {col.components?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    {col.components.length} component(s)
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      
      case COMPONENT_TYPES.SOCIAL:
        return (
          <div style={component.styles} className="flex items-center justify-center gap-4">
            {Object.entries(component.links).map(([platform, url]) => 
              url ? (
                <div 
                  key={platform}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: component.iconColor || '#1B4F72' }}
                >
                  <span className="text-white text-xs font-bold uppercase">
                    {platform.charAt(0)}
                  </span>
                </div>
              ) : null
            )}
            {!Object.values(component.links).some(Boolean) && (
              <span className="text-sm text-slate-400">Add social links in settings</span>
            )}
          </div>
        );
      
      case COMPONENT_TYPES.QUOTE:
        return (
          <blockquote style={component.styles}>
            <p>"{component.content}"</p>
            {component.author && (
              <footer className="mt-2 text-sm" style={{ fontStyle: 'normal', color: '#64748b' }}>
                — {component.author}
              </footer>
            )}
          </blockquote>
        );
      
      case COMPONENT_TYPES.LIST:
        return (
          <ul style={{ ...component.styles, listStyleType: component.listStyle }}>
            {component.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
      
      case COMPONENT_TYPES.VIDEO:
        return (
          <div style={component.styles}>
            {component.thumbnailUrl ? (
              <div className="relative inline-block">
                <img 
                  src={component.thumbnailUrl} 
                  alt="Video thumbnail" 
                  style={{ maxWidth: '100%', borderRadius: component.styles.borderRadius }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                  >
                    <div 
                      className="w-0 h-0 ml-1"
                      style={{
                        borderTop: '10px solid transparent',
                        borderBottom: '10px solid transparent',
                        borderLeft: `15px solid ${component.playButtonColor || '#fff'}`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <MousePointer className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <span className="text-sm text-slate-500">Add video thumbnail URL</span>
              </div>
            )}
          </div>
        );
      
      case COMPONENT_TYPES.FOOTER:
        return (
          <div style={component.styles}>
            <p style={{ fontWeight: '600', marginBottom: '8px' }}>{component.companyName}</p>
            <p>{component.address}</p>
            {component.phone && <p>{component.phone}</p>}
            {component.email && <p>{component.email}</p>}
          </div>
        );
      
      case COMPONENT_TYPES.UNSUBSCRIBE:
        return (
          <div style={component.styles}>
            <a href="#unsubscribe" style={{ color: component.styles.color, textDecoration: 'underline' }}>
              {component.text}
            </a>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div
      ref={(node) => drag(drop(node))}
      data-testid={`canvas-component-${index}`}
      className={`
        relative group mb-2 rounded-lg transition-all
        ${isDragging ? 'opacity-50' : ''}
        ${isSelected ? 'ring-2 ring-navy-500 ring-offset-2' : 'hover:ring-2 hover:ring-slate-300'}
      `}
      onClick={() => selectComponent(index)}
    >
      {/* Drag Handle */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full pr-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>
      
      {/* Component Content */}
      <div className="p-3 bg-white border border-slate-200 rounded-lg min-h-[40px]">
        {renderComponent()}
      </div>
      
      {/* Action Buttons */}
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); duplicateComponent(index); }}
          className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
          title="Duplicate"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); removeComponent(index); }}
          className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// Properties Panel
const PropertiesPanel = ({ component, updateComponent, onClose }) => {
  if (!component) {
    return (
      <div className="p-6 text-center text-slate-500 h-full flex flex-col items-center justify-center">
        <Settings className="w-12 h-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm font-medium">Select a component</p>
        <p className="text-xs text-slate-400 mt-1">Click on any component to edit its properties</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    updateComponent({ ...component, [field]: value });
  };

  const handleStyleChange = (field, value) => {
    updateComponent({
      ...component,
      styles: { ...component.styles, [field]: value },
    });
  };

  const handleContainerStyleChange = (field, value) => {
    updateComponent({
      ...component,
      containerStyles: { ...component.containerStyles, [field]: value },
    });
  };

  const ColorPicker = ({ value, onChange, label }) => (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      <div className="flex gap-2">
        <Input
          type="color"
          value={value || '#333333'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-8 p-0.5 cursor-pointer"
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 text-xs h-8"
        />
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            onClick={() => onChange(color)}
            className={`w-5 h-5 rounded border-2 transition-all ${value === color ? 'border-navy-500 scale-110' : 'border-transparent hover:scale-110'}`}
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </div>
    </div>
  );

  const AlignmentPicker = ({ value, onChange }) => (
    <div className="flex gap-1">
      {[
        { align: 'left', icon: AlignLeft },
        { align: 'center', icon: AlignCenter },
        { align: 'right', icon: AlignRight },
      ].map(({ align, icon: Icon }) => (
        <button
          key={align}
          onClick={() => onChange(align)}
          className={`p-2 rounded transition-colors ${
            value === align 
              ? 'bg-navy-100 text-navy-600' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Icon className="w-4 h-4" />
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 space-y-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <h3 className="font-semibold text-slate-700 capitalize text-sm">
          {component.type.replace('_', ' ')} Settings
        </h3>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Heading Component */}
      {component.type === COMPONENT_TYPES.HEADING && (
        <>
          <div>
            <Label className="text-xs">Heading Text</Label>
            <Textarea
              value={component.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={2}
              className="mt-1 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Heading Level</Label>
            <Select value={component.level} onValueChange={(v) => handleChange('level', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="h1">H1 - Main Title</SelectItem>
                <SelectItem value="h2">H2 - Section Title</SelectItem>
                <SelectItem value="h3">H3 - Sub Title</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Font Family</Label>
            <Select value={component.styles.fontFamily} onValueChange={(v) => handleStyleChange('fontFamily', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_FAMILIES.map((font) => (
                  <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Font Size</Label>
              <Select value={component.styles.fontSize} onValueChange={(v) => handleStyleChange('fontSize', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Font Weight</Label>
              <Select value={component.styles.fontWeight} onValueChange={(v) => handleStyleChange('fontWeight', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="400">Normal</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semi Bold</SelectItem>
                  <SelectItem value="700">Bold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
          <div>
            <Label className="text-xs mb-2 block">Alignment</Label>
            <AlignmentPicker 
              value={component.styles.textAlign} 
              onChange={(v) => handleStyleChange('textAlign', v)} 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Line Height</Label>
              <Input
                value={component.styles.lineHeight}
                onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
                className="mt-1"
                placeholder="1.3"
              />
            </div>
            <div>
              <Label className="text-xs">Letter Spacing</Label>
              <Input
                value={component.styles.letterSpacing}
                onChange={(e) => handleStyleChange('letterSpacing', e.target.value)}
                className="mt-1"
                placeholder="0px"
              />
            </div>
          </div>
        </>
      )}

      {/* Text Component */}
      {component.type === COMPONENT_TYPES.TEXT && (
        <>
          <div>
            <Label className="text-xs">Content</Label>
            <Textarea
              value={component.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={4}
              className="mt-1 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Font Family</Label>
            <Select value={component.styles.fontFamily} onValueChange={(v) => handleStyleChange('fontFamily', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_FAMILIES.map((font) => (
                  <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Font Size</Label>
              <Select value={component.styles.fontSize} onValueChange={(v) => handleStyleChange('fontSize', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Font Weight</Label>
              <Select value={component.styles.fontWeight || '400'} onValueChange={(v) => handleStyleChange('fontWeight', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="400">Normal</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semi Bold</SelectItem>
                  <SelectItem value="700">Bold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">Text Style</Label>
            <div className="flex gap-1">
              <button
                onClick={() => handleStyleChange('fontWeight', component.styles.fontWeight === '700' ? '400' : '700')}
                className={`p-2 rounded ${component.styles.fontWeight === '700' ? 'bg-navy-100 text-navy-600' : 'bg-slate-100'}`}
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleStyleChange('fontStyle', component.styles.fontStyle === 'italic' ? 'normal' : 'italic')}
                className={`p-2 rounded ${component.styles.fontStyle === 'italic' ? 'bg-navy-100 text-navy-600' : 'bg-slate-100'}`}
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleStyleChange('textDecoration', component.styles.textDecoration === 'underline' ? 'none' : 'underline')}
                className={`p-2 rounded ${component.styles.textDecoration === 'underline' ? 'bg-navy-100 text-navy-600' : 'bg-slate-100'}`}
              >
                <Underline className="w-4 h-4" />
              </button>
            </div>
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
          <div>
            <Label className="text-xs mb-2 block">Alignment</Label>
            <AlignmentPicker 
              value={component.styles.textAlign} 
              onChange={(v) => handleStyleChange('textAlign', v)} 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Line Height</Label>
              <Input
                value={component.styles.lineHeight}
                onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
                className="mt-1"
                placeholder="1.6"
              />
            </div>
            <div>
              <Label className="text-xs">Padding</Label>
              <Input
                value={component.styles.padding}
                onChange={(e) => handleStyleChange('padding', e.target.value)}
                className="mt-1"
                placeholder="10px 0"
              />
            </div>
          </div>
        </>
      )}

      {/* Image Component */}
      {component.type === COMPONENT_TYPES.IMAGE && (
        <>
          <div>
            <Label className="text-xs">Image URL</Label>
            <Input
              value={component.src}
              onChange={(e) => handleChange('src', e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Alt Text</Label>
            <Input
              value={component.alt}
              onChange={(e) => handleChange('alt', e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Width</Label>
              <Input
                value={component.width}
                onChange={(e) => handleChange('width', e.target.value)}
                placeholder="100%"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max Width</Label>
              <Input
                value={component.maxWidth}
                onChange={(e) => handleChange('maxWidth', e.target.value)}
                placeholder="600px"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Link URL (optional)</Label>
            <Input
              value={component.link}
              onChange={(e) => handleChange('link', e.target.value)}
              placeholder="https://..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Alignment</Label>
            <AlignmentPicker 
              value={component.styles.textAlign} 
              onChange={(v) => handleStyleChange('textAlign', v)} 
            />
          </div>
          <div>
            <Label className="text-xs">Border Radius</Label>
            <Input
              value={component.styles.borderRadius}
              onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
              placeholder="8px"
              className="mt-1"
            />
          </div>
        </>
      )}

      {/* Button Component */}
      {component.type === COMPONENT_TYPES.BUTTON && (
        <>
          <div>
            <Label className="text-xs">Button Text</Label>
            <Input
              value={component.text}
              onChange={(e) => handleChange('text', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Link URL</Label>
            <Input
              value={component.link}
              onChange={(e) => handleChange('link', e.target.value)}
              className="mt-1"
            />
          </div>
          <ColorPicker 
            label="Background Color" 
            value={component.styles.backgroundColor} 
            onChange={(v) => handleStyleChange('backgroundColor', v)} 
          />
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Font Size</Label>
              <Select value={component.styles.fontSize} onValueChange={(v) => handleStyleChange('fontSize', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_SIZES.slice(0, 6).map((size) => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Font Weight</Label>
              <Select value={component.styles.fontWeight} onValueChange={(v) => handleStyleChange('fontWeight', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="400">Normal</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semi Bold</SelectItem>
                  <SelectItem value="700">Bold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Padding</Label>
              <Input
                value={component.styles.padding}
                onChange={(e) => handleStyleChange('padding', e.target.value)}
                placeholder="14px 32px"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Border Radius</Label>
              <Input
                value={component.styles.borderRadius}
                onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
                placeholder="8px"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">Button Position</Label>
            <AlignmentPicker 
              value={component.containerStyles?.textAlign || 'center'} 
              onChange={(v) => handleContainerStyleChange('textAlign', v)} 
            />
          </div>
        </>
      )}

      {/* Divider Component */}
      {component.type === COMPONENT_TYPES.DIVIDER && (
        <>
          <ColorPicker 
            label="Line Color" 
            value={component.styles.borderColor} 
            onChange={(v) => handleStyleChange('borderColor', v)} 
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Line Width</Label>
              <Input
                value={component.styles.borderWidth}
                onChange={(e) => handleStyleChange('borderWidth', e.target.value)}
                placeholder="1px"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Style</Label>
              <Select value={component.styles.borderStyle} onValueChange={(v) => handleStyleChange('borderStyle', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BORDER_STYLES.map((style) => (
                    <SelectItem key={style} value={style}>{style}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Margin</Label>
            <Input
              value={component.styles.margin}
              onChange={(e) => handleStyleChange('margin', e.target.value)}
              placeholder="20px 0"
              className="mt-1"
            />
          </div>
        </>
      )}

      {/* Spacer Component */}
      {component.type === COMPONENT_TYPES.SPACER && (
        <div>
          <Label className="text-xs">Height</Label>
          <Input
            value={component.height}
            onChange={(e) => handleChange('height', e.target.value)}
            placeholder="30px"
            className="mt-1"
          />
        </div>
      )}

      {/* Quote Component */}
      {component.type === COMPONENT_TYPES.QUOTE && (
        <>
          <div>
            <Label className="text-xs">Quote Text</Label>
            <Textarea
              value={component.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Author</Label>
            <Input
              value={component.author}
              onChange={(e) => handleChange('author', e.target.value)}
              className="mt-1"
            />
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
          <ColorPicker 
            label="Border Color" 
            value={component.styles.borderLeft?.split(' ')[2] || '#D4A03A'} 
            onChange={(v) => handleStyleChange('borderLeft', `4px solid ${v}`)} 
          />
          <ColorPicker 
            label="Background" 
            value={component.styles.backgroundColor} 
            onChange={(v) => handleStyleChange('backgroundColor', v)} 
          />
        </>
      )}

      {/* List Component */}
      {component.type === COMPONENT_TYPES.LIST && (
        <>
          <div>
            <Label className="text-xs">List Items (one per line)</Label>
            <Textarea
              value={component.items.join('\n')}
              onChange={(e) => handleChange('items', e.target.value.split('\n'))}
              rows={5}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">List Style</Label>
            <Select value={component.listStyle} onValueChange={(v) => handleChange('listStyle', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disc">Bullet (•)</SelectItem>
                <SelectItem value="decimal">Numbered (1.)</SelectItem>
                <SelectItem value="circle">Circle (○)</SelectItem>
                <SelectItem value="square">Square (■)</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
        </>
      )}

      {/* Social Component */}
      {component.type === COMPONENT_TYPES.SOCIAL && (
        <>
          <div>
            <Label className="text-xs">Facebook URL</Label>
            <Input
              value={component.links.facebook}
              onChange={(e) => handleChange('links', { ...component.links, facebook: e.target.value })}
              placeholder="https://facebook.com/..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Twitter URL</Label>
            <Input
              value={component.links.twitter}
              onChange={(e) => handleChange('links', { ...component.links, twitter: e.target.value })}
              placeholder="https://twitter.com/..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">LinkedIn URL</Label>
            <Input
              value={component.links.linkedin}
              onChange={(e) => handleChange('links', { ...component.links, linkedin: e.target.value })}
              placeholder="https://linkedin.com/..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Instagram URL</Label>
            <Input
              value={component.links.instagram}
              onChange={(e) => handleChange('links', { ...component.links, instagram: e.target.value })}
              placeholder="https://instagram.com/..."
              className="mt-1"
            />
          </div>
          <ColorPicker 
            label="Icon Color" 
            value={component.iconColor} 
            onChange={(v) => handleChange('iconColor', v)} 
          />
        </>
      )}

      {/* Footer Component */}
      {component.type === COMPONENT_TYPES.FOOTER && (
        <>
          <div>
            <Label className="text-xs">Company Name</Label>
            <Input
              value={component.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Address</Label>
            <Textarea
              value={component.address}
              onChange={(e) => handleChange('address', e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input
              value={component.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              value={component.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="mt-1"
            />
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
          <ColorPicker 
            label="Background" 
            value={component.styles.backgroundColor} 
            onChange={(v) => handleStyleChange('backgroundColor', v)} 
          />
        </>
      )}

      {/* Unsubscribe Component */}
      {component.type === COMPONENT_TYPES.UNSUBSCRIBE && (
        <>
          <div>
            <Label className="text-xs">Link Text</Label>
            <Input
              value={component.text}
              onChange={(e) => handleChange('text', e.target.value)}
              className="mt-1"
            />
          </div>
          <ColorPicker 
            label="Text Color" 
            value={component.styles.color} 
            onChange={(v) => handleStyleChange('color', v)} 
          />
        </>
      )}
    </div>
  );
};

// Drop Zone
const CanvasDropZone = ({ children, onDrop }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'NEW_COMPONENT',
    drop: (item) => onDrop(item.type),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`
        min-h-[500px] p-4 rounded-lg transition-all border-2
        ${isOver 
          ? 'bg-navy-50 border-dashed border-navy-400' 
          : 'bg-white border-transparent'
        }
      `}
    >
      {children}
      {isOver && (
        <div className="text-center py-6 text-navy-500 text-sm font-medium animate-pulse">
          Drop component here
        </div>
      )}
    </div>
  );
};

// Main Builder Page
export default function AdminEmailBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'create';
  const type = searchParams.get('type') || 'draft';
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [components, setComponents] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('editor');
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Form State
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [category, setCategory] = useState('custom');
  
  // Modals
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Token
  const token = localStorage.getItem('grc_token');

  // Load existing draft/template
  useEffect(() => {
    if (id && mode === 'edit') {
      loadExisting();
    }
  }, [id, mode]);

  const loadExisting = async () => {
    setLoading(true);
    try {
      const endpoint = type === 'draft' 
        ? `/admin/email-drafts/${id}`
        : `/admin/custom-templates/${id}`;
      
      const response = await adminAPI.get(endpoint);
      const data = response.data;
      
      setName(data.name || data.display_name || '');
      setSubject(data.subject || '');
      setPreviewText(data.preview_text || '');
      setCategory(data.category || 'custom');
      setComponents(data.components || []);
    } catch (error) {
      console.error('Failed to load:', error);
      toast.error('Failed to load content');
    }
    setLoading(false);
  };

  // Component operations
  const addComponent = useCallback((type) => {
    const newComponent = { ...JSON.parse(JSON.stringify(DEFAULT_COMPONENTS[type])), id: Date.now() };
    setComponents(prev => [...prev, newComponent]);
    setSelectedIndex(components.length);
  }, [components.length]);

  const moveComponent = useCallback((fromIndex, toIndex) => {
    setComponents(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
    setSelectedIndex(toIndex);
  }, []);

  const removeComponent = useCallback((index) => {
    setComponents(prev => prev.filter((_, i) => i !== index));
    setSelectedIndex(null);
  }, []);

  const updateComponent = useCallback((updatedComponent) => {
    setComponents(prev => prev.map((c, i) => i === selectedIndex ? updatedComponent : c));
  }, [selectedIndex]);

  const duplicateComponent = useCallback((index) => {
    const componentToDuplicate = components[index];
    const duplicated = { ...JSON.parse(JSON.stringify(componentToDuplicate)), id: Date.now() };
    setComponents(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, duplicated);
      return updated;
    });
    setSelectedIndex(index + 1);
  }, [components]);

  // Generate HTML
  const generateHtml = useCallback(() => {
    const componentHtml = components.map(component => {
      switch (component.type) {
        case COMPONENT_TYPES.HEADING:
          return `<${component.level || 'h1'} style="${styleToString(component.styles)}">${component.content}</${component.level || 'h1'}>`;
        
        case COMPONENT_TYPES.TEXT:
          return `<p style="${styleToString(component.styles)}">${component.content}</p>`;
        
        case COMPONENT_TYPES.IMAGE:
          const imgHtml = `<img src="${component.src}" alt="${component.alt}" style="width: ${component.width}; max-width: ${component.maxWidth || '100%'}; border-radius: ${component.styles.borderRadius || '0'};">`;
          const imgWrapper = component.link 
            ? `<a href="${component.link}">${imgHtml}</a>`
            : imgHtml;
          return `<div style="text-align: ${component.styles.textAlign}; padding: ${component.styles.padding || '10px 0'};">${imgWrapper}</div>`;
        
        case COMPONENT_TYPES.BUTTON:
          return `<div style="${styleToString(component.containerStyles || { textAlign: 'center', padding: '20px 0' })}"><a href="${component.link}" style="${styleToString(component.styles)}; text-decoration: none;">${component.text}</a></div>`;
        
        case COMPONENT_TYPES.DIVIDER:
          return `<hr style="border: none; border-top: ${component.styles.borderWidth} ${component.styles.borderStyle} ${component.styles.borderColor}; margin: ${component.styles.margin}; width: ${component.styles.width};">`;
        
        case COMPONENT_TYPES.SPACER:
          return `<div style="height: ${component.height};"></div>`;
        
        case COMPONENT_TYPES.QUOTE:
          return `<blockquote style="${styleToString(component.styles)}"><p style="margin: 0;">"${component.content}"</p>${component.author ? `<footer style="margin-top: 10px; font-style: normal; font-size: 14px;">— ${component.author}</footer>` : ''}</blockquote>`;
        
        case COMPONENT_TYPES.LIST:
          const listItems = component.items.map(item => `<li>${item}</li>`).join('');
          return `<ul style="${styleToString(component.styles)}; list-style-type: ${component.listStyle};">${listItems}</ul>`;
        
        case COMPONENT_TYPES.SOCIAL:
          const socialLinks = Object.entries(component.links)
            .filter(([, url]) => url)
            .map(([platform, url]) => `<a href="${url}" style="display: inline-block; width: 32px; height: 32px; background-color: ${component.iconColor || '#1B4F72'}; border-radius: 50%; text-align: center; line-height: 32px; color: white; text-decoration: none; margin: 0 8px; font-weight: bold;">${platform.charAt(0).toUpperCase()}</a>`)
            .join('');
          return `<div style="${styleToString(component.styles)}">${socialLinks}</div>`;
        
        case COMPONENT_TYPES.FOOTER:
          return `<div style="${styleToString(component.styles)}"><p style="font-weight: 600; margin-bottom: 8px;">${component.companyName}</p><p style="margin: 4px 0;">${component.address}</p>${component.phone ? `<p style="margin: 4px 0;">${component.phone}</p>` : ''}${component.email ? `<p style="margin: 4px 0;">${component.email}</p>` : ''}</div>`;
        
        case COMPONENT_TYPES.UNSUBSCRIBE:
          return `<div style="${styleToString(component.styles)}"><a href="{{unsubscribe_url}}" style="color: ${component.styles.color}; text-decoration: underline;">${component.text}</a></div>`;
        
        default:
          return '';
      }
    }).join('\n');
    
    return componentHtml;
  }, [components]);

  const styleToString = (styles) => {
    if (!styles) return '';
    return Object.entries(styles)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');
  };

  // Preview
  useEffect(() => {
    if (activeTab === 'preview') {
      setPreviewHtml(generateHtml());
    }
  }, [activeTab, components, generateHtml]);

  // Save Draft
  const saveDraft = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    setSaving(true);
    try {
      const htmlContent = generateHtml();
      const payload = {
        name,
        subject,
        preview_text: previewText,
        html_content: htmlContent,
        components,
        category,
      };
      
      if (id && mode === 'edit') {
        await adminAPI.put(`/admin/email-drafts/${id}`, payload);
        toast.success('Draft saved');
      } else {
        const response = await adminAPI.post('/admin/email-drafts', payload);
        toast.success('Draft created');
        navigate(`/admin/email-builder/${response.data.draft.id}?mode=edit&type=draft`);
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save draft');
    }
    setSaving(false);
  };

  // Publish as Template
  const publishAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSaving(true);
    try {
      // First save draft if new
      if (!id) {
        const htmlContent = generateHtml();
        const draftResponse = await adminAPI.post('/admin/email-drafts', {
          name,
          subject,
          preview_text: previewText,
          html_content: htmlContent,
          components,
          category,
        });
        
        // Then publish
        await adminAPI.post(`/admin/email-drafts/${draftResponse.data.draft.id}/publish`, {
          template_name: templateName.replace(/\s+/g, '_').toLowerCase(),
        });
      } else {
        await adminAPI.post(`/admin/email-drafts/${id}/publish`, {
          template_name: templateName.replace(/\s+/g, '_').toLowerCase(),
        });
      }
      
      toast.success('Published as template!');
      setPublishModalOpen(false);
      navigate('/admin/email-templates');
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-4" data-testid="email-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-navy-700">
                {mode === 'edit' ? 'Edit Email' : 'Create Email'}
              </h1>
              <p className="text-sm text-slate-500">
                Drag and drop components to build your email
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            <Button
              onClick={() => setPublishModalOpen(true)}
              disabled={saving || components.length === 0}
            >
              <Send className="w-4 h-4 mr-2" />
              Publish as Template
            </Button>
          </div>
        </div>

        {/* Email Settings */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Email Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Email Campaign"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Subject Line</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Your subject here..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Preview Text</Label>
                <Input
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Text shown in inbox..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Editor */}
        <div className="flex gap-4" style={{ height: 'calc(100vh - 300px)' }}>
          {/* Component Palette */}
          <div className="w-44 bg-slate-50 rounded-lg p-3 overflow-y-auto">
            <h3 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Components
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <PaletteItem type={COMPONENT_TYPES.HEADING} icon={Heading1} label="Heading" />
              <PaletteItem type={COMPONENT_TYPES.TEXT} icon={Type} label="Text" />
              <PaletteItem type={COMPONENT_TYPES.IMAGE} icon={Image} label="Image" />
              <PaletteItem type={COMPONENT_TYPES.BUTTON} icon={Square} label="Button" />
              <PaletteItem type={COMPONENT_TYPES.DIVIDER} icon={Minus} label="Divider" />
              <PaletteItem type={COMPONENT_TYPES.SPACER} icon={ChevronDown} label="Spacer" />
              <PaletteItem type={COMPONENT_TYPES.COLUMNS_2} icon={Columns} label="2 Col" />
              <PaletteItem type={COMPONENT_TYPES.COLUMNS_3} icon={Columns} label="3 Col" />
              <PaletteItem type={COMPONENT_TYPES.QUOTE} icon={Quote} label="Quote" />
              <PaletteItem type={COMPONENT_TYPES.LIST} icon={List} label="List" />
              <PaletteItem type={COMPONENT_TYPES.SOCIAL} icon={Link2} label="Social" />
              <PaletteItem type={COMPONENT_TYPES.FOOTER} icon={Mail} label="Footer" />
              <PaletteItem type={COMPONENT_TYPES.UNSUBSCRIBE} icon={X} label="Unsub" />
            </div>
          </div>

          {/* Canvas & Preview */}
          <div className="flex-1 bg-slate-100 rounded-lg overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <div className="bg-white border-b px-4">
                <TabsList className="h-12">
                  <TabsTrigger value="editor" className="gap-2">
                    <Layout className="w-4 h-4" />
                    Editor
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="gap-2">
                    <Eye className="w-4 h-4" />
                    Preview
                  </TabsTrigger>
                  <TabsTrigger value="code" className="gap-2">
                    <Code className="w-4 h-4" />
                    HTML
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="editor" className="flex-1 overflow-y-auto p-4 m-0">
                <div className="max-w-2xl mx-auto">
                  <CanvasDropZone onDrop={addComponent}>
                    {components.length === 0 ? (
                      <div className="text-center py-20 text-slate-400">
                        <Plus className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium">Start building your email</p>
                        <p className="text-sm mt-2">Drag components from the left panel</p>
                      </div>
                    ) : (
                      components.map((component, index) => (
                        <CanvasComponent
                          key={component.id || index}
                          component={component}
                          index={index}
                          moveComponent={moveComponent}
                          removeComponent={removeComponent}
                          selectComponent={setSelectedIndex}
                          isSelected={selectedIndex === index}
                          duplicateComponent={duplicateComponent}
                        />
                      ))
                    )}
                  </CanvasDropZone>
                </div>
              </TabsContent>
              
              <TabsContent value="preview" className="flex-1 overflow-y-auto p-4 m-0">
                <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Email Header Preview */}
                  <div className="bg-navy-800 p-6 text-center">
                    <div className="text-2xl font-bold text-white">
                      GRC<span className="font-normal opacity-80">educators</span>
                    </div>
                    <div className="text-[#D4A03A] text-xs tracking-widest mt-1">
                      YOUR PATH TO EXCELLENCE
                    </div>
                  </div>
                  
                  {/* Subject Line */}
                  <div className="bg-slate-100 p-4 border-b">
                    <p className="text-sm text-slate-500">Subject:</p>
                    <p className="font-semibold">{subject || 'No subject'}</p>
                    {previewText && (
                      <p className="text-sm text-slate-400 mt-1">{previewText}</p>
                    )}
                  </div>
                  
                  {/* Email Body */}
                  <div 
                    className="p-8"
                    dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-center text-slate-400">No content yet</p>' }}
                  />
                  
                  {/* Footer */}
                  <div className="bg-slate-100 p-6 text-center text-sm text-slate-500">
                    <p className="font-medium">GRC Educators</p>
                    <p className="mt-1">Governance, Risk & Compliance Training</p>
                    <p className="mt-4 text-xs">
                      © {new Date().getFullYear()} GRC Educators. All rights reserved.
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="code" className="flex-1 overflow-y-auto p-4 m-0">
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Generated HTML</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(generateHtml());
                        toast.success('HTML copied to clipboard');
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy HTML
                    </Button>
                  </div>
                  <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <pre>{generateHtml() || '<!-- No components added yet -->'}</pre>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Properties Panel */}
          <div className="w-72 bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-sm text-slate-700">Properties</span>
            </div>
            <PropertiesPanel
              component={selectedIndex !== null ? components[selectedIndex] : null}
              updateComponent={updateComponent}
              onClose={() => setSelectedIndex(null)}
            />
          </div>
        </div>

        {/* Publish Modal */}
        <Dialog open={publishModalOpen} onOpenChange={setPublishModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Publish as Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-500">
                Publishing will create a reusable email template that can be used for campaigns.
              </p>
              <div>
                <Label>Template Name (used as ID)</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="my_custom_template"
                  className="mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Will be converted to: {templateName.replace(/\s+/g, '_').toLowerCase() || 'template_name'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPublishModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={publishAsTemplate} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publish
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DndProvider>
  );
}
